import React, { useEffect, useState, useMemo, useRef } from 'react';
import { 
  Box, Container, Typography, CircularProgress, Paper, IconButton, 
  Collapse, Stack, TextField, Table, TableBody, TableCell, TableHead, TableRow 
} from '@mui/material';
import Heading from '../../Components/heading.tsx';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Polyline, Marker, OverlayView, InfoWindow } from '@react-google-maps/api';
import Draggable from 'react-draggable';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CallIcon from '@mui/icons-material/Call';
import deliveryTruckIcon from '../../../assets/delivery_truck.png';
import institutionIcon from '../../../assets/institution.png';
import { getVehicleRoute, getCurrentLocation, getInstitutionVehicleMapping } from './action.ts';
import { getStops } from '../Stops/action.ts';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import FooterLogo from '../../Components/footer.tsx';

import { useVehicleTracking } from '../../../WebApp/Hooks/useVehicleTracking.ts';

const themeGradient = 'linear-gradient(135deg, rgb(234, 31, 37) 20%, rgb(0, 0, 0) 100%)';

const styles = {
  container: { display: 'flex', flexDirection: 'column' as const, height: '100vh' },
  header: { marginTop: '30px', marginBottom: '0px' },
  mapContainer: { flex: 1, borderRadius: '8px', overflow: 'hidden', width: '100%', minHeight: '600px', position: 'relative' as const },
  loaderContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }
};

const mapContainerStyle = { width: '100%', height: '100%' };
const defaultCenter = { lat: 20.5937, lng: 78.9629 };
const libraries: ("geometry" | "places")[] = ["geometry"];

const noCalendarIconStyle = {
  '& input::-webkit-calendar-picker-indicator': { display: 'none', WebkitAppearance: 'none' },
  '& input': { cursor: 'pointer' }
};

const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDistanceInMeters = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371e3;
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * 
    Math.sin(dLon / 2) * Math.sin(dLon / 2); 
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)); 
  return R * c; 
};

const CloseSvgIcon = () => (
  <svg viewBox="0 0 384 512" width="12" height="12" fill="currentColor">
    <path d="M231.6 256l130.1-130.1c4.7-4.7 4.7-12.3 0-17l-19.6-19.6c-4.7-4.7-12.3-4.7-17 0L195 219.4 64.9 89.3c-4.7-4.7-12.3-4.7-17 0l-19.6 19.6c-4.7 4.7-4.7 12.3 0 17L158.4 256 28.3 386.1c-4.7 4.7-4.7 12.3 0 17l19.6 19.6c4.7 4.7 12.3 4.7 17 0L195 292.6l130.1 130.1c4.7 4.7 12.3 4.7 17 0l19.6-19.6c4.7-4.7 4.7-12.3 0-17L231.6 256z"/>
  </svg>
);

const RoutesScreen: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const vehicleId = params.get('vehicle_id');
  const token = params.get('token');
  const userId = params.get('user_id');
  const userName = params.get('user_name') || '';
  const companiesId = params.get('companies_id') || '';
  const companyName = params.get('company_name') || '';
  const vehicleNumber = params.get('vehicle_number') || '';
  const navigate = useNavigate();
  const isSTMS = companyName.toUpperCase() === 'STMS' || String(companiesId) === '95' || String(companiesId) === '11';
  const [routePath, setRoutePath] = useState<{ lat: number; lng: number }[]>([]);
  const [polylineString, setPolylineString] = useState<string>("");
  const [startPoint, setStartPoint] = useState<any>(null);
  const [mainVehicleLoc, setMainVehicleLoc] = useState<any>(null);
  const [companyVehiclesLocs, setCompanyVehiclesLocs] = useState<any[]>([]);
  const [geofencedStops, setGeofencedStops] = useState<any[]>([]); 
  const [institutionData, setInstitutionData] = useState<any[]>([]);
  const [selectedInstitution, setSelectedInstitution] = useState<any>(null);
  const [selectedVehicleLoc, setSelectedVehicleLoc] = useState<any>(null); 
  const [selectedStop, setSelectedStop] = useState<any>(null);
  const [selectedEvent, setSelectedEvent] = useState<any>(null); 
  const [stopRoutingInfo, setStopRoutingInfo] = useState<{distance: string, duration: string} | null>(null);
  const [drivingDistanceText, setDrivingDistanceText] = useState<string | null>(null); 
  const [combinedEvents, setCombinedEvents] = useState<any[]>([]);
  const [isFetchingData, setIsFetchingData] = useState<boolean>(true);
  const dragRef = useRef<HTMLDivElement>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [distance, setDistance] = useState<string>('');
  const [duration, setDuration] = useState<string>('');
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  const [currentZoom, setCurrentZoom] = useState<number>(13);
  const todayStr = getTodayDateString();
  const [fromDate, setFromDate] = useState<string>(`${todayStr}T00:00`);
  const [toDate, setToDate] = useState<string>(`${todayStr}T23:59`);
  const [dayWiseData, setDayWiseData] = useState<any[] | null>(null);
  const [showTableInTab, setShowTableInTab] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<{lat: number, lng: number} | null>(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: "AIzaSyA5izcJ7TGIrfklTCrMx3Q2JmeK_zld1sc",
    libraries: libraries 
  });

  const { livePosition, isConnected } = useVehicleTracking(vehicleId, token);
  useEffect(() => {
    if (livePosition) {
      const newPoint = { lat: livePosition.lat, lng: livePosition.lng };
      setMainVehicleLoc(newPoint);
      setRoutePath(prev => [...prev, newPoint]);
      if (mapRef) {
        mapRef.panTo(newPoint);
      }
    }
  }, [livePosition, mapRef]);

  useEffect(() => {
    console.log(`🌐 [COMPONENT STATE] WebSocket is currently: ${isConnected ? 'CONNECTED🟢' : 'DISCONNECTED🔴'}`);
  }, [isConnected]);

  const handleBack = () => {
    navigate(`/wv/dashboard?user_name=${userName}&vehicle_id=${vehicleId}&user_id=${userId}&token=${token}`);
  };

  const isToday = (dateString: string) => {
    if (!dateString) return false;
    const today = new Date();
    const eventDate = new Date(dateString);
    return (
      today.getFullYear() === eventDate.getFullYear() &&
      today.getMonth() === eventDate.getMonth() &&
      today.getDate() === eventDate.getDate()
    );
  };

  const formatDateTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; 
    return `${day}/${month} - ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
  };

  const formatTimeOnly = (dateStr: string) => {
    const d = new Date(dateStr);
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; 
    return `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
  };

  const formatDuration = (event: any) => {
    if (event.duration_hours) {
      const hrs = Math.floor(event.duration_hours);
      const mins = Math.floor((event.duration_hours - hrs) * 60);
      return `${hrs} hr ${mins} min`;
    } else if (event.duration_sec) {
      const hrs = Math.floor(event.duration_sec / 3600);
      const mins = Math.floor((event.duration_sec % 3600) / 60);
      if (hrs > 0) return `${hrs} hr ${mins} min`;
      return `${mins} mins`;
    }
    return "N/A";
  };

  const formatDateDDMMYYYY = (dateString: string) => {
    if (!dateString) return '';
    const parts = dateString.split('-');
    if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
    return dateString;
  };

  const handleInputClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const input = e.target as HTMLInputElement;
    if (input.tagName === 'INPUT' && 'showPicker' in input) {
      try { (input as any).showPicker(); } catch (error) { console.log("Picker not supported here log"); }
    }
  };

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error("Error fetching user location:", error);
        }
      );
    }
  }, []);

  const fetchRouteData = (startStr: string, endStr: string, isRowClick = false) => {
    if (!vehicleId) return;
    setIsFetchingData(true);
    const apiStart = startStr.replace('T', ' ') + ':00';
    const apiEnd = endStr.replace('T', ' ') + ':59';

    getVehicleRoute(
      vehicleId,
      apiStart,
      apiEnd,
      (response) => {
        const payload = response?.data || response;

        if (payload) {
          if (!isRowClick && payload.day_wise_distance && Array.isArray(payload.day_wise_distance)) {
            const filteredDayWise = payload.day_wise_distance.filter((day: any) => Number(day.distance_km) >= 1);
            setDayWiseData(filteredDayWise);

            if (filteredDayWise.length > 0) {
              handleRowClick(filteredDayWise[0].date);
            } else {
              setShowTableInTab(true);
              setIsFetchingData(false);
            }
            return;
          }

          if (payload.distance_km !== undefined) setDistance(`${Number(payload.distance_km).toFixed(2)} km`);
          if (payload.time_taken) {
            const { seconds, hours } = payload.time_taken;
            if (seconds) {
              const h = Math.floor(Number(seconds) / 3600);
              const m = Math.floor((Number(seconds) % 3600) / 60);
              setDuration(`${h}h ${m}m`);
            } else if (hours) setDuration(`${Number(hours).toFixed(1)} hrs`);
          }

          setPolylineString(payload.polyline || "");
          if (payload.start) setStartPoint(payload.start);
          if (payload.current_location) setMainVehicleLoc(payload.current_location);

          const stops = (payload.stop_events || []).map((e: any) => ({ ...e, eventType: 'stop' }));
          const idles = (payload.idle_events || []).map((e: any) => ({ ...e, eventType: 'idle' }));
          
          const trafficHolds = (payload.traffic_hold_events || []).filter(
            (event: any) => !!event.geofence_name
          ).map((e: any) => ({ ...e, eventType: 'traffic_hold' })); 

          let mergedRaw = isSTMS ? [...trafficHolds, ...stops] : [...stops, ...idles, ...trafficHolds];
          let merged = mergedRaw.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
          
          const groupedEvents: any[] = [];       
          merged.forEach((rawEvent) => {
            const event = { ...rawEvent };
            const lat = parseFloat(event.start_latitude || event.lat);
            const lng = parseFloat(event.start_longitude || event.lng);

            if (isNaN(lat) || isNaN(lng)) return;

            let foundCluster = null;
            for (const cluster of groupedEvents) {
              if (getDistanceInMeters(lat, lng, cluster.lat, cluster.lng) <= 100) {
                foundCluster = cluster;
                break;
              }
            }

            if (foundCluster) {
              const lastEvent = foundCluster.events[foundCluster.events.length - 1];
              const lastEndTime = lastEvent.end_time ? new Date(lastEvent.end_time).getTime() : null;
              const newStartTime = event.start_time ? new Date(event.start_time).getTime() : null;
              
              if (lastEndTime && newStartTime && (newStartTime - lastEndTime) <= 180000) {
                lastEvent.end_time = event.end_time || lastEvent.end_time;
                lastEvent.duration_sec = (lastEvent.duration_sec || 0) + (event.duration_sec || 0);
                lastEvent.duration_hours = (lastEvent.duration_hours || 0) + (event.duration_hours || 0);
                if (event.eventType === 'stop' && lastEvent.eventType !== 'stop') {
                  lastEvent.eventType = 'stop';
                  lastEvent.status = event.status || lastEvent.status;
                  lastEvent.stop_type = event.stop_type || lastEvent.stop_type;
                }
              } else {
                foundCluster.events.push(event);
              }

              if (event.geofence_name && !foundCluster.geofence_name) {
                foundCluster.geofence_name = event.geofence_name;
              }
            } else {
              groupedEvents.push({
                lat,
                lng,
                geofence_name: event.geofence_name,
                events: [event]
              });
            }
          });

          let currentSeq = 1;
          const allEventsFlat: any[] = [];
          groupedEvents.forEach(cluster => {
            cluster.events.forEach((ev: any) => allEventsFlat.push(ev));
          });
          
          allEventsFlat.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
          
          allEventsFlat.forEach(ev => {
            ev.sequenceId = currentSeq++;
          });

          setCombinedEvents(groupedEvents);
        } else {
          setPolylineString("");
          setCombinedEvents([]);
          setStartPoint(null);
          setMainVehicleLoc(null);
        }
        setIsFetchingData(false);
      },
      (errorMsg) => {
        toast.error(errorMsg?.message || "Failed to fetch routes");
        setIsFetchingData(false);
      }
    );
  };

  const handleFromDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = event.target.value;
    setFromDate(newDate);
    if (new Date(newDate) > new Date(toDate)) {
      setToDate(newDate);
      fetchRouteData(newDate, newDate);
    } else {
      fetchRouteData(newDate, toDate);
    }
  };

  const handleToDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newDate = event.target.value;
    if (new Date(newDate) < new Date(fromDate)) {
      toast.warning("To date cannot be earlier than From date.");
      newDate = fromDate;
    }
    setToDate(newDate);
    fetchRouteData(fromDate, newDate);
  };

  const handleRowClick = (dateStr: string) => {
    setSelectedDate(dateStr);
    setShowTableInTab(true);
    fetchRouteData(`${dateStr}T00:00`, `${dateStr}T23:59`, true);
  };

  useEffect(() => {
    if (!selectedVehicleLoc || (!mainVehicleLoc && !userLocation) || !window.google) {
      setDrivingDistanceText(null);
      return;
    }

    setDrivingDistanceText("Calculating distance...");

    try {
      const originLat = mainVehicleLoc ? parseFloat(mainVehicleLoc.lat || mainVehicleLoc.latitude) : userLocation?.lat;
      const originLng = mainVehicleLoc ? parseFloat(mainVehicleLoc.lng || mainVehicleLoc.longitude) : userLocation?.lng;

      const origin = new window.google.maps.LatLng(originLat as number, originLng as number);
      
      const destination = new window.google.maps.LatLng(
        parseFloat(selectedVehicleLoc.lat || selectedVehicleLoc.latitude),
        parseFloat(selectedVehicleLoc.lng || selectedVehicleLoc.longitude)
      );

      const service = new window.google.maps.DistanceMatrixService();

      service.getDistanceMatrix(
        {
          origins: [origin],
          destinations: [destination],
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (response, status) => {
          if (status === 'OK' && response) {
            const element = response.rows[0].elements[0];
            if (element.status === 'OK') {
              setDrivingDistanceText(`${element.distance.text} (${element.duration.text} drive) away`);
            } else {
              setDrivingDistanceText("Route distance unavailable");
            }
          } else {
            setDrivingDistanceText("Route distance unavailable");
          }
        }
      );
    } catch (error) {
      console.error("Distance Matrix Error:", error);
      setDrivingDistanceText("Distance unavailable");
    }
  }, [selectedVehicleLoc, mainVehicleLoc, userLocation]);

  useEffect(() => {
    if (!selectedStop || (!mainVehicleLoc && !userLocation) || !window.google) {
      setStopRoutingInfo(null);
      return;
    }

    setStopRoutingInfo({ distance: "Calculating...", duration: "Calculating..." });

    try {
      const originLat = mainVehicleLoc ? parseFloat(mainVehicleLoc.lat || mainVehicleLoc.latitude) : userLocation?.lat;
      const originLng = mainVehicleLoc ? parseFloat(mainVehicleLoc.lng || mainVehicleLoc.longitude) : userLocation?.lng;

      const origin = new window.google.maps.LatLng(originLat as number, originLng as number);
      
      const destination = new window.google.maps.LatLng(
        parseFloat(selectedStop.center_lat),
        parseFloat(selectedStop.center_lng)
      );

      const service = new window.google.maps.DistanceMatrixService();

      service.getDistanceMatrix(
        {
          origins: [origin],
          destinations: [destination],
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (response, status) => {
          if (status === 'OK' && response) {
            const element = response.rows[0].elements[0];
            if (element.status === 'OK') {
              setStopRoutingInfo({
                distance: element.distance.text,
                duration: element.duration.text
              });
            } else {
              setStopRoutingInfo({ distance: "Unavailable", duration: "Unavailable" });
            }
          } else {
            setStopRoutingInfo({ distance: "Unavailable", duration: "Unavailable" });
          }
        }
      );
    } catch (error) {
      console.error("Distance Matrix Error for Stop:", error);
      setStopRoutingInfo({ distance: "Error", duration: "Error" });
    }
  }, [selectedStop, mainVehicleLoc, userLocation]);

  useEffect(() => {
    if (isSTMS && vehicleId) {
      getStops(
        vehicleId, 
        100,
        0,
        (response: any) => {
          let stopsArray: any[] = [];
          if (Array.isArray(response)) {
            stopsArray = response;
          } else if (response?.data && Array.isArray(response.data)) {
            stopsArray = response.data;
          } else if (response?.data?.data && Array.isArray(response.data.data)) {
            stopsArray = response.data.data;
          }
          
          const approvedStops = stopsArray.filter((stop: any) => stop.status === 'approved');
          setGeofencedStops(approvedStops);
        }, 
        (errorMsg: any) => {
          console.error("Failed to fetch geofenced stops:", errorMsg);
        }
      );

      getInstitutionVehicleMapping(
        [vehicleId],
        userId || '', 
        10,
        0,
        (response: any) => {
          if (response?.data && Array.isArray(response.data)) {
            setInstitutionData(response.data);
          }
        }, 
        (errorMsg: any) => {
          console.error("Failed to fetch institution vehicle mapping:", errorMsg);
        }
      );

    } else {
      setGeofencedStops([]);
    }
  }, [vehicleId, isSTMS, userId]);

  useEffect(() => {
    fetchRouteData(fromDate, toDate);
  }, [vehicleId]);

  useEffect(() => {
    if (isSTMS) {
      setCompanyVehiclesLocs([]);
      return;
    }

    if (isToday(toDate) || isToday(fromDate)) {
      getCurrentLocation(companiesId, (response) => {
        const data = response?.data || response;
        const locationsArray = Array.isArray(data) ? data : (data ? [data] : []);

        const filteredLocs = locationsArray.filter(
          (loc: any) => String(loc.vehicle_id) !== String(vehicleId)
        );

        setCompanyVehiclesLocs(filteredLocs);
      }, (errorMsg) => {
        console.error("Failed to fetch current location:", errorMsg);
      });
    } else {
      setCompanyVehiclesLocs([]);
    }
  }, [companiesId, toDate, fromDate, vehicleId, isSTMS]);

  useEffect(() => {
    if (isLoaded && polylineString) {
      try {
        const decodedPath = window.google.maps.geometry.encoding.decodePath(polylineString);
        const formattedPath = decodedPath.map(p => ({ lat: p.lat(), lng: p.lng() }));
        setRoutePath(formattedPath);
      } catch (err) {
        console.error("Failed to decode polyline", err);
      }
    } else {
      setRoutePath([]);
    }
  }, [isLoaded, polylineString]);

  const center = useMemo(() => {
    if (mainVehicleLoc) {
      return { 
        lat: parseFloat(mainVehicleLoc.lat || mainVehicleLoc.latitude), 
        lng: parseFloat(mainVehicleLoc.lng || mainVehicleLoc.longitude) 
      };
    }
    if (routePath.length > 0) return routePath[0];
    
    if (geofencedStops.length > 0) {
      return { 
        lat: parseFloat(geofencedStops[0].center_lat), 
        lng: parseFloat(geofencedStops[0].center_lng) 
      };
    }

    if (userLocation) return userLocation;
    
    return defaultCenter;
  }, [mainVehicleLoc, routePath, geofencedStops, userLocation]);

  useEffect(() => {
    if (mapRef && window.google) {
      if (routePath.length === 0 && !mainVehicleLoc && geofencedStops.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        let hasValidPoints = false;

        geofencedStops.forEach((stop) => {
          const lat = parseFloat(stop.center_lat);
          const lng = parseFloat(stop.center_lng);
          
          if (!isNaN(lat) && !isNaN(lng)) {
            bounds.extend(new window.google.maps.LatLng(lat, lng));
            hasValidPoints = true;
          }
        });

        institutionData.forEach((inst) => {
          const lat = parseFloat(inst.institution_lat);
          const lng = parseFloat(inst.institution_lng);
          if (!isNaN(lat) && !isNaN(lng)) {
            bounds.extend(new window.google.maps.LatLng(lat, lng));
            hasValidPoints = true;
          }
        });

        if (hasValidPoints) {
          mapRef.fitBounds(bounds);
          if (geofencedStops.length === 1 && institutionData.length === 0) {
            mapRef.setZoom(14);
          }
        }
      }
    }
  }, [mapRef, routePath, mainVehicleLoc, geofencedStops, institutionData]);


  let activeEventGeofenced = false;
  let activeEventName = "Stop Location";
  let activeBadgeColor = '#ff9900'; 

  if (selectedEvent) {
    activeEventGeofenced = !!selectedEvent.geofence_name;
    activeEventName = selectedEvent.geofence_name || "Stop Location";
    
    if (!activeEventGeofenced && isSTMS) {
      const matchedGeo = geofencedStops.find(geo => {
        const geoLat = parseFloat(geo.center_lat);
        const geoLng = parseFloat(geo.center_lng);
        const radius = parseFloat(geo.radius_meters || '100');
        return !isNaN(geoLat) && !isNaN(geoLng) && getDistanceInMeters(selectedEvent.lat, selectedEvent.lng, geoLat, geoLng) <= radius;
      });
      if (matchedGeo) {
        activeEventGeofenced = true;
        activeEventName = matchedGeo.name || "Geofence Stop";
      }
    }

    const stopEvent = selectedEvent.events.find((e: any) => e.eventType === 'stop');
    const hasIdle = selectedEvent.events.some((e: any) => e.eventType === 'idle');

    if (stopEvent) {
      if (activeEventGeofenced) {
        activeBadgeColor = '#2e7d32'; 
      } else {
        const status = String(stopEvent.status || stopEvent.stop_type || '').toLowerCase();
        if (status.includes('parked') || status.includes('short stop') || status.includes('short_stop')) {
          activeBadgeColor = '#ff9900'; 
        } else {
          activeBadgeColor = '#e60000'; 
        }
      }
    } else if (hasIdle) {
      activeBadgeColor = '#ff9900'; 
    } else if (activeEventGeofenced) {
      activeBadgeColor = '#2e7d32'; 
    }
  }


  const curLat = mainVehicleLoc ? parseFloat(mainVehicleLoc.lat || mainVehicleLoc.latitude) : (userLocation?.lat ?? null);
  const curLng = mainVehicleLoc ? parseFloat(mainVehicleLoc.lng || mainVehicleLoc.longitude) : (userLocation?.lng ?? null);

  return (
    <Container style={styles.container}>
      <Box style={styles.header}>
        <Heading title="Routes" onBackClick={handleBack}  vehicle={vehicleNumber} />
      </Box>

      <style>{`
        @keyframes pulseBlinkRed {
          0% { transform: scale(0.8); opacity: 1; box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.7); }
          70% { transform: scale(1.5); opacity: 0.5; box-shadow: 0 0 0 15px rgba(255, 0, 0, 0); }
          100% { transform: scale(0.8); opacity: 1; box-shadow: 0 0 0 0 rgba(255, 0, 0, 0); }
        }
        @keyframes pulseBlinkBlue {
          0% { transform: scale(0.8); opacity: 1; box-shadow: 0 0 0 0 rgba(0, 85, 255, 0.7); }
          70% { transform: scale(1.5); opacity: 0.5; box-shadow: 0 0 0 15px rgba(0, 85, 255, 0); }
          100% { transform: scale(0.8); opacity: 1; box-shadow: 0 0 0 0 rgba(0, 85, 255, 0); }
        }
        .blinking-marker-red { 
          width: 16px; height: 16px; background-color: #e60000; 
          border-radius: 50%; border: 2px solid #fff;
          animation: pulseBlinkRed 1.5s infinite; transform: translate(-50%, -50%); 
        }
        .blinking-marker-blue { 
          width: 16px; height: 16px; background-color: #0055ff; 
          border-radius: 50%; border: 2px solid #fff;
          animation: pulseBlinkBlue 1.5s infinite; transform: translate(-50%, -50%); 
        }

        /* --- Info Window Styles --- */
        .gm-ui-hover-effect {
          display: none !important; 
        }
        /* ADDED: Hide the default empty header space in Google Maps */
        .gm-style .gm-style-iw-ch {
          display: none !important;
        }
        .gm-style .gm-style-iw-c {
          padding: 12px 14px !important; 
          border-radius: 8px !important;
          box-shadow: 0 4px 15px rgba(0,0,0,0.15) !important;
        }
        .gm-style .gm-style-iw-d {
          overflow: visible !important; 
          padding: 0 !important;
        }
      `}</style>

      <Box style={styles.mapContainer}>
        {!isConnected && vehicleId && !isFetchingData && (
          <Box sx={{ position: 'absolute', top: 0, width: '100%', bgcolor: '#fffbeb', color: '#92400e', p: 1, textAlign: 'center', zIndex: 10, fontSize: '13px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            Live connection lost, reconnecting...
          </Box>
        )}

        {!isLoaded || isFetchingData ? (
          <Box style={styles.loaderContainer}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading Map & Routes...</Typography>
          </Box>
        ) : (
          <GoogleMap 
            mapContainerStyle={mapContainerStyle} 
            center={center} 
            zoom={13} 
            onLoad={(map) => {
              setMapRef(map);
              setCurrentZoom(map.getZoom() || 13);
            }} 
            onUnmount={() => setMapRef(null)} 
            onZoomChanged={() => {
              if (mapRef) setCurrentZoom(mapRef.getZoom() || 13);
            }}
            onClick={() => {
              setSelectedVehicleLoc(null);
              setSelectedStop(null);
              setSelectedEvent(null); 
              setSelectedInstitution(null); 
            }}
          >
            {routePath.length > 0 && (
              <Polyline 
                path={routePath} 
                options={{ 
                  strokeColor: '#2b78e4', strokeOpacity: 0.9, strokeWeight: 6,
                  icons: [{ icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, strokeColor: '#000000', fillColor: '#333333', fillOpacity: 1, scale: 2.5 }, offset: '100%', repeat: '100px' }]
                }} 
              />
            )}

            {startPoint && (
              <Marker
                position={{ lat: parseFloat(startPoint.lat), lng: parseFloat(startPoint.lng) }}
                icon={{ path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, fillColor: '#0055ff', fillOpacity: 1, strokeWeight: 1, strokeColor: '#ffffff', scale: 6, rotation: 0 }}
              />
            )}

            {institutionData.map((inst, idx) => {
              const lat = parseFloat(inst.institution_lat);
              const lng = parseFloat(inst.institution_lng);
              
              if (isNaN(lat) || isNaN(lng)) return null;

              return (
                <Marker
                  key={`institution-marker-${idx}`}
                  position={{ lat, lng }}
                  title={inst.institution_name}
                  icon={{
                    url: institutionIcon,
                    scaledSize: new window.google.maps.Size(40, 40), 
                    anchor: new window.google.maps.Point(20, 20) 
                  }}
                  onClick={() => setSelectedInstitution(inst)}
                />
              );
            })}
            
            {selectedInstitution && (
              <InfoWindow
                position={{ 
                  lat: parseFloat(selectedInstitution.institution_lat), 
                  lng: parseFloat(selectedInstitution.institution_lng) 
                }}
                onCloseClick={() => setSelectedInstitution(null)}
                options={{ pixelOffset: new window.google.maps.Size(0, -20), disableAutoPan: true, headerDisabled: true } as any}
              >
                <Box sx={{ position: 'relative', pr: '24px', minWidth: '160px', textAlign: 'left', fontFamily: 'sans-serif' }}>
                  <IconButton
                    onClick={() => setSelectedInstitution(null)}
                    disableRipple
                    sx={{ position: 'absolute', top: -4, right: -12, p: 0.5, color: '#9ca3af', '&:hover': { color: '#6b7280', background: 'transparent' } }}
                  >
                    <CloseSvgIcon />
                  </IconButton>

                  <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1f2933', mb: 0.5, lineHeight: 1.2 }}>
                    {selectedInstitution.institution_name}
                  </Typography>
                  <Typography sx={{ fontSize: '14px', color: '#6b7280' }}>
                    <strong style={{ color: '#1f2933' }}>Vehicle:</strong> {selectedInstitution.number_plate}
                  </Typography>
                  <Typography sx={{ fontSize: '14px', color: '#6b7280' }}>
                    <strong style={{ color: '#1f2933' }}>Driver:</strong> {selectedInstitution.driver_name}
                  </Typography>
                  <Typography sx={{ fontSize: '14px', color: '#6b7280' }}>
                    <strong style={{ color: '#1f2933' }}>Phone:</strong> {selectedInstitution.driver_number}
                  </Typography>
                </Box>
              </InfoWindow>
            )}

            {combinedEvents.map((cluster, idx) => {
              if (curLat !== null && curLng !== null) {
                const dist = getDistanceInMeters(cluster.lat, cluster.lng, curLat, curLng);
                if (dist <= 50) return null;
              }

              const stopEvent = cluster.events.find((e: any) => e.eventType === 'stop');
              const hasIdle = cluster.events.some((e: any) => e.eventType === 'idle');
              
              let markerColor = '#ff9900'; 
              
              if (stopEvent) {
                if (cluster.geofence_name) {
                  markerColor = '#2e7d32'; 
                } else {
                  const status = String(stopEvent.status || stopEvent.stop_type || '').toLowerCase();
                  if (status.includes('parked') || status.includes('short stop') || status.includes('short_stop')) {
                    markerColor = '#ff9900'; 
                  } else {
                    markerColor = '#e60000'; 
                  }
                }
              } else if (hasIdle) {
                markerColor = '#ff9900'; 
              } else if (cluster.geofence_name) {
                markerColor = '#2e7d32'; 
              }

              return (
                <Marker
                  key={`event-marker-${idx}`}
                  position={{ lat: cluster.lat, lng: cluster.lng }}
                  title={stopEvent ? "Stop Event" : hasIdle ? "Idle Event" : "Event"}
                  label={{ text: String(cluster.events[0].sequenceId), color: 'white', fontWeight: 'bold', fontSize: '15px' }}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    fillColor: markerColor,
                    fillOpacity: 1,
                    strokeWeight: 2,
                    strokeColor: '#FFFFFF',
                    scale: 12 
                  }}
                  onClick={() => setSelectedEvent(cluster)} 
                />
              );
            })}

            {selectedEvent && (
              <InfoWindow 
                position={{ lat: selectedEvent.lat, lng: selectedEvent.lng }} 
                onCloseClick={() => setSelectedEvent(null)}
                options={{ pixelOffset: new window.google.maps.Size(0, -10), disableAutoPan: true, headerDisabled: true } as any}
              >
                <Box sx={{ position: 'relative', minWidth: '240px', pr: '24px', textAlign: 'left', fontFamily: 'sans-serif' }}>
                  <IconButton
                    onClick={() => setSelectedEvent(null)}
                    disableRipple
                    sx={{ position: 'absolute', top: -4, right: -12, p: 0.5, color: '#9ca3af', '&:hover': { color: '#6b7280', background: 'transparent' } }}
                  >
                    <CloseSvgIcon />
                  </IconButton>

                  <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1f2933', mb: 1, lineHeight: 1.2 }}>
                    {activeEventName}
                  </Typography>

                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    {selectedEvent.events.map((ev: any, idx: number) => {
                      return (
                        <Box 
                          key={`event-list-${idx}`} 
                          sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            py: 1,
                            borderBottom: idx !== selectedEvent.events.length - 1 ? '1px solid #e0e0e0' : 'none' 
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Box 
                              sx={{ 
                                width: 22, 
                                height: 22, 
                                borderRadius: "50%", 
                                backgroundColor: activeBadgeColor, 
                                color: "white", 
                                display: "flex", 
                                justifyContent: "center", 
                                alignItems: "center", 
                                fontSize: "14px", 
                                fontWeight: "bold" 
                              }}
                            >
                              {ev.sequenceId}
                            </Box>
                            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: '#1f2933' }}>
                              {idx === 0 ? formatDateTime(ev.start_time) : formatTimeOnly(ev.start_time)}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: "13px", color: "#6b7280", fontWeight: 500, ml: 2 }}>
                            {formatDuration(ev)}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </InfoWindow>
            )}

            {(() => {
              let nextSeq = combinedEvents.reduce((max, cluster) => {
                return Math.max(max, ...cluster.events.map((e: any) => e.sequenceId || 0));
              }, 0) + 1;

              return geofencedStops.map((stop, idx) => {
                const lat = parseFloat(stop.center_lat);
                const lng = parseFloat(stop.center_lng);
                
                if (isNaN(lat) || isNaN(lng)) return null;

                const matchedCluster = combinedEvents.find(cluster => 
                  cluster.geofence_name === stop.name || cluster.geofence_name === stop.geofence_name
                );

                const isReached = !!matchedCluster;
                const fillColor = isReached ? '#2e7d32' : '#ff9900';
                
                let labelText = "";
                if (isReached) {
                  labelText = String(matchedCluster.events[0].sequenceId);
                } else {
                  labelText = String(nextSeq++);
                }
                if (curLat !== null && curLng !== null) {
                  const dist = getDistanceInMeters(lat, lng, curLat, curLng);
                  if (dist <= 50) return null;
                }

                return (
                  <Marker 
                    key={`geofence-stop-marker-${idx}`}
                    position={{ lat, lng }}
                    label={{ 
                      text: labelText, 
                      color: 'white', 
                      fontWeight: 'bold', 
                      fontSize: '14px' 
                    }}
                    icon={{ 
                      path: window.google.maps.SymbolPath.CIRCLE, 
                      fillColor: fillColor, 
                      fillOpacity: 1, 
                      strokeWeight: 3,       
                      strokeColor: '#FFFFFF', 
                      scale: 14              
                    }}
                    onClick={() => {
                      setSelectedStop(stop);
                    }}
                  />
                );
              });
            })()}

            {selectedStop && (() => {
              const displayName = (selectedStop.geofence_name || (selectedStop.name || '').trim() || '');

              return (
                <InfoWindow
                  position={{ 
                    lat: parseFloat(selectedStop.center_lat), 
                    lng: parseFloat(selectedStop.center_lng) 
                  }}
                  onCloseClick={() => setSelectedStop(null)}
                  options={{ pixelOffset: new window.google.maps.Size(0, -15), disableAutoPan: true, headerDisabled: true } as any}
                >
                  <Box sx={{ position: 'relative', pr: '24px', minWidth: '160px', textAlign: 'left', fontFamily: 'sans-serif' }}>
                    <IconButton
                      onClick={() => setSelectedStop(null)}
                      disableRipple
                      sx={{ position: 'absolute', top: -4, right: -12, p: 0.5, color: '#9ca3af', '&:hover': { color: '#6b7280', background: 'transparent' } }}
                    >
                      <CloseSvgIcon />
                    </IconButton>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1f2933', mb: 0.5, lineHeight: 1.2 }}>
                      {displayName}
                    </Typography>
                    
                    <Box sx={{ pt: 0.5 }}>
                      {stopRoutingInfo ? (
                        <>
                          <Typography sx={{ fontSize: '14px', color: '#6b7280' }}>
                            <strong style={{ color: '#1f2933' }}>Distance:</strong> {stopRoutingInfo.distance}
                          </Typography>
                          <Typography sx={{ fontSize: '14px', color: '#6b7280' }}>
                            <strong style={{ color: '#1f2933' }}>Est. Time:</strong> {stopRoutingInfo.duration}
                          </Typography>
                        </>
                      ) : (
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#6b7280' }}>
                          Calculating route info...
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </InfoWindow>
              );
            })()}

            {mainVehicleLoc ? (
              <OverlayView 
                position={{ 
                  lat: parseFloat(mainVehicleLoc.lat || mainVehicleLoc.latitude), 
                  lng: parseFloat(mainVehicleLoc.lng || mainVehicleLoc.longitude) 
                }} 
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div className="blinking-marker-red" />
              </OverlayView>
            ) : userLocation && (
              <OverlayView 
                position={userLocation} 
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div className="blinking-marker-blue" />
              </OverlayView>
            )}

            {(() => {
              const locationCounts: Record<string, number> = {};
              
              const degreePerPixel = 360 / (256 * Math.pow(2, currentZoom));
              const gridSize = degreePerPixel * 40; 
              const offsetDegrees = degreePerPixel * 40;
              
              return companyVehiclesLocs.map((loc, idx) => {
                let lat = parseFloat(loc.lat || loc.latitude);
                let lng = parseFloat(loc.lng || loc.longitude);
                if (isNaN(lat) || isNaN(lng)) return null;
                const gridX = Math.floor(lng / gridSize);
                const gridY = Math.floor(lat / gridSize);
                const gridKey = `${gridX},${gridY}`;
                
                if (!locationCounts[gridKey]) {
                  locationCounts[gridKey] = 0;
                }
                const count = locationCounts[gridKey]++;
                if (count > 0) {
                  const direction = count % 2 === 0 ? -1 : 1;
                  const multiplier = Math.ceil(count / 2);
                  lng = lng + (offsetDegrees * multiplier * direction);
                }

                return (
                  <React.Fragment key={`vehicle-container-${idx}`}>
                    <Marker
                      position={{ lat, lng }}
                      icon={{
                        url: deliveryTruckIcon,
                        scaledSize: new window.google.maps.Size(40, 40), 
                        anchor: new window.google.maps.Point(20, 20)
                      }}
                      onClick={() => setSelectedVehicleLoc(loc)}
                    />
                  </React.Fragment>
                );
              });
            })()}

            {selectedVehicleLoc && (
              <InfoWindow
                position={{ 
                  lat: parseFloat(selectedVehicleLoc.lat || selectedVehicleLoc.latitude), 
                  lng: parseFloat(selectedVehicleLoc.lng || selectedVehicleLoc.longitude) 
                }}
                onCloseClick={() => setSelectedVehicleLoc(null)}
                options={{ pixelOffset: new window.google.maps.Size(0, -25), disableAutoPan: true, headerDisabled: true } as any}
              >
                <Box sx={{ position: 'relative', pr: '24px', minWidth: '160px', textAlign: 'left', fontFamily: 'sans-serif' }}>
                  
                  <IconButton
                    onClick={() => setSelectedVehicleLoc(null)}
                    disableRipple
                    sx={{ 
                      position: 'absolute', 
                      top: -4, 
                      right: -12, 
                      p: 0.5, 
                      color: '#9ca3af',
                      '&:hover': { color: '#6b7280', background: 'transparent' }
                    }}
                  >
                    <CloseSvgIcon />
                  </IconButton>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1f2933', lineHeight: 1.2 }}>
                      {selectedVehicleLoc.driver_name || "Unknown Driver"}
                    </Typography>

                    {selectedVehicleLoc.driver_phone && (
                      <IconButton 
                        size="small"
                        sx={{ p: 0, color: '#16a34a' }}
                        onClick={() => {
                          const countryCode = selectedVehicleLoc.driver_country_code || '91';
                          window.location.href = `tel:+${countryCode}${selectedVehicleLoc.driver_phone}`;
                        }}
                      >
                        <CallIcon sx={{ fontSize: '20px' }} />
                      </IconButton>
                    )}
                  </Box>

                  {drivingDistanceText && (
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#ea1f25', mt: 0.5 }}>
                      {drivingDistanceText}
                    </Typography>
                  )}
                </Box>
              </InfoWindow>
            )}

          </GoogleMap>
        )}

      <Draggable 
        nodeRef={dragRef} 
        axis="y" 
        bounds={{ top: -300, bottom: 0 }}
        cancel="input, button, .MuiInputBase-root, .MuiIconButton-root, .no-drag"
      >
        <Paper 
          ref={dragRef}
          elevation={6} 
          sx={{ 
            position: 'absolute', 
            bottom: '49px', 
            left: 0, 
            right: 0,
            padding: '16px', 
            borderRadius: '16px 16px 0 0',
            zIndex: 10,
            cursor: 'grab', 
            backgroundColor: 'white'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: isMinimized ? 0 : 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 0.5 }}>
                {(distance || duration) && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                      {distance && (
                    <Box sx={{ background: themeGradient, px: 2.5, py: 0.8, borderRadius: '24px', boxShadow: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#fff', fontSize: '13px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <DirectionsCarIcon sx={{ fontSize: '16px', color: '#fff' }} /> {distance}
                      </Typography>
                    </Box>
                  )}
                  {duration && (
                    <Box sx={{ background: themeGradient, px: 2.5, py: 0.8, borderRadius: '24px', boxShadow: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#fff', fontSize: '13px' }}>
                        ⏱ {duration}
                      </Typography>
                    </Box>
                  )}
                  </Box>
                )}
              </Box>
            </Box>

            <IconButton size="small" onClick={() => setIsMinimized(!isMinimized)} sx={{ p: 0.5 }}>
              {isMinimized ? <ExpandLessIcon fontSize="large" /> : <ExpandMoreIcon fontSize="large" />}
            </IconButton>
          </Box>

          <Collapse in={!isMinimized} timeout="auto" unmountOnExit>
            <Box sx={{ padding: '0px' }}>
              <Stack direction="row" spacing={2} sx={{ marginBottom: '20px', marginTop: '12px' }}>
                <TextField 
                  label="From" 
                  type="datetime-local" 
                  value={fromDate} 
                  onChange={handleFromDateChange} 
                  onClick={handleInputClick}
                  fullWidth 
                  size="small" 
                  InputLabelProps={{ shrink: true }}
                  sx={noCalendarIconStyle} 
                />
                <TextField 
                  label="To" 
                  type="datetime-local" 
                  value={toDate} 
                  onChange={handleToDateChange} 
                  onClick={handleInputClick}
                  fullWidth 
                  size="small" 
                  InputLabelProps={{ shrink: true }}
                  sx={noCalendarIconStyle} 
                />
              </Stack>

              {dayWiseData && showTableInTab && (
  <Paper 
    className="no-drag"
    variant="outlined" 
    sx={{ 
      mb: 2, 
      maxHeight: '150px', 
      overflowY: 'auto', 
      borderRadius: '8px',
      '&::-webkit-scrollbar': { width: '6px' },
      '&::-webkit-scrollbar-thumb': { backgroundColor: '#bdbdbd', borderRadius: '4px' }
    }}
  >
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow sx={{ '& th': { bgcolor: '#f1f3f4', py: 0.5, fontSize: '0.8rem' } }}>
          <TableCell><strong>Date</strong></TableCell>
          <TableCell align="right"><strong>Distance</strong></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {dayWiseData.map((row, index) => (
          <TableRow 
            key={index} 
            className="no-drag" 
            hover
            selected={selectedDate === row.date}
            onClick={() => handleRowClick(row.date)}
            sx={{ 
              cursor: 'pointer', 
              '& td': { py: 0.5, fontSize: '0.8rem' },
              '&.Mui-selected': { backgroundColor: '#e8f0fe' },
              '&.Mui-selected:hover': { backgroundColor: '#d2e3fc' },
              '&:hover': { backgroundColor: '#f1f3f4' }
            }}
          >
            <TableCell>{formatDateDDMMYYYY(row.date)}</TableCell>
            <TableCell align="right">{row.distance_km} km</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Paper>
)}
            </Box>
          </Collapse>
        </Paper>
      </Draggable>

      </Box>
      <FooterLogo/>
    </Container>
  );
};

export default RoutesScreen;